import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import TextRotationNoneIcon from '@mui/icons-material/TextRotationNone'
import { IconButton, Stack, Tooltip } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { EntryWithRelated, PromptWithRelated } from '../../models/inspections'
import { OpenNotesField } from '../../pages/DviSingleInspection/components/OpenNotesField'
import { PromptNotesField } from '../../pages/DviSingleInspection/components/PromptNotesField'
import { useRewriteMutation } from '../../redux/api/independentApi'
import { useUpdateInspectionEntryMutation } from '../../redux/api/inspectionApi'
import { PromptInfoTypeEnum } from '../../sdk'

interface Props {
  prompt: PromptWithRelated
  entry: EntryWithRelated
  setCustomerNotesLoading: React.Dispatch<React.SetStateAction<boolean>>
}

const SingleTechnicianNotes = ({
  prompt,
  entry,
  setCustomerNotesLoading
}: Props) => {
  // a local control is needed because the prompt.info is not updated in time when user copy it to customer notes
  const [notes, setNotes] = useState('')
  const [updateEntry, { isLoading: updatingEntry }] =
    useUpdateInspectionEntryMutation()
  const { accessToken, shopId } = useAccessTokenAndShopId()

  useEffect(() => {
    const temp =
      prompt.info!.type === PromptInfoTypeEnum.OpenEnded
        ? prompt.info!.answer
        : prompt.info!.technicianNotes
    setNotes(temp || '')
  }, [prompt.info])
  const addNotes = useCallback(async () => {
    if (!notes) return
    const customerNotes = entry.info!.customerNotes
      ? entry.info!.customerNotes.trim() + '\n' + notes
      : notes
    setCustomerNotesLoading(true)
    await updateEntry({
      authorization: accessToken,
      entryEntry: {
        ...entry,
        info: {
          ...entry.info!,
          customerNotes
        }
      }
    })
    setCustomerNotesLoading(false)
  }, [accessToken, entry, notes, setCustomerNotesLoading, updateEntry])
  const updateCallback = useCallback((updatedPrompt: PromptWithRelated) => {
    if (updatedPrompt.info!.type === PromptInfoTypeEnum.OpenEnded) {
      setNotes(updatedPrompt.info!.answer || '')
    } else {
      setNotes(updatedPrompt.info!.technicianNotes || '')
    }
  }, [])
  const [rewrite, { isLoading: rewriting }] = useRewriteMutation()

  const rewriteHandler = useCallback(async () => {
    if (!notes) return
    setCustomerNotesLoading(true)
    const result = await rewrite({
      authorization: accessToken,
      rewriteRequest: {
        customPrompt:
          "You are a service advisor of an auto repair shop. Based on the following findings from technician, provide a more explanatory and brief note to the customer and highlight why the identified concerns should be resolved immediately if there is any. Don't use any punctuation around the text. Don't include any salutations or any form of closing/sign-off.",
        existingText: notes,
        shop: shopId
      }
    }).unwrap()
    await updateEntry({
      authorization: accessToken,
      entryEntry: {
        ...entry,
        info: {
          ...entry.info!,
          customerNotes: result
        }
      }
    })
    setCustomerNotesLoading(false)
  }, [
    accessToken,
    entry,
    notes,
    rewrite,
    setCustomerNotesLoading,
    shopId,
    updateEntry
  ])

  return (
    <Stack direction={'row'} spacing={1} alignItems={'center'}>
      {prompt.info!.type === PromptInfoTypeEnum.OpenEnded ? (
        <OpenNotesField prompt={prompt} updateCallback={updateCallback} />
      ) : (
        <PromptNotesField prompt={prompt} updateCallback={updateCallback} />
      )}
      <Stack spacing={1}>
        <Tooltip title="Copy to customer notes">
          <IconButton
            size="small"
            onClick={addNotes}
            disabled={!notes || updatingEntry || rewriting}>
            <TextRotationNoneIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Generate Customer Notes using AI">
          <IconButton
            size="small"
            onClick={rewriteHandler}
            disabled={!notes || updatingEntry || rewriting}>
            <AutoFixHighIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  )
}

export default SingleTechnicianNotes
